"use client"

import { Suspense, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react"
import { usePathname } from "next/navigation"
import { getCloseAttemptHandler, useGlobalDialogStore } from "@/stores/use-global-dialog-store"
import { Skeleton } from "@ui/shad"

import { BookIdProvider, useBookId } from "@/hooks/use-book-id"

import { AppDialog } from "./app-dialog"
import { DialogEntryContext } from "./dialog-entry-context"

function DialogBookContext({ children, initialBookId }: { children: ReactNode; initialBookId?: string }) {
  const currentBookId = useBookId()
  const [bookId] = useState(initialBookId ?? currentBookId)
  return <BookIdProvider bookId={bookId}>{children}</BookIdProvider>
}

export default function GlobalDialog() {
  const { isOpen, contentStack, closeDialogsOnNavigation, popDialog, dialogContentClasses, preventEscape, scrollable } =
    useGlobalDialogStore()
  const pathname = usePathname()
  const previousPathname = useRef(pathname)
  const wasOpenOnMount = useRef(isOpen)
  const activeContentRef = useRef<HTMLDivElement>(null)
  const previousStackDepth = useRef(0)

  useLayoutEffect(() => {
    const previousDepth = previousStackDepth.current
    previousStackDepth.current = contentStack.length
    // The host stays mounted on push, so Radix's initial autofocus does not run again.
    if (!previousDepth || contentStack.length <= previousDepth) return
    const content = activeContentRef.current
    if (!content || content.contains(document.activeElement)) return
    const candidates = content.querySelectorAll<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex="0"]'
    )
    const firstVisible = Array.from(candidates).find(
      (element) =>
        element.tabIndex >= 0 &&
        element.getClientRects().length > 0 &&
        getComputedStyle(element).visibility !== "hidden"
    )
    firstVisible?.focus({ preventScroll: true })
  }, [contentStack.length])

  useEffect(() => {
    const pathnameChanged = previousPathname.current !== pathname
    previousPathname.current = pathname

    if (!wasOpenOnMount.current && !pathnameChanged) return

    wasOpenOnMount.current = false
    closeDialogsOnNavigation()
  }, [pathname, closeDialogsOnNavigation])

  return (
    <AppDialog
      variant="custom"
      title="Dialog"
      open={isOpen}
      onOpenChange={() => (getCloseAttemptHandler() ?? popDialog)()}
      customClassName={dialogContentClasses}
      scrollable={scrollable}
      contentProps={{
        ...(contentStack.at(-1)?.hasAccessibleTitle
          ? { "aria-labelledby": `app-dialog-title-${contentStack.at(-1)?.id}` }
          : {}),
        onEscapeKeyDown:
          preventEscape || contentStack.at(-1)?.dismiss === "none" ? (e) => e.preventDefault() : undefined,
        onInteractOutside: (ev) => {
          if (contentStack.at(-1)?.dismiss !== "all") {
            ev.preventDefault()
            return
          }
          const target = ev.target as Element
          // External UI rendered directly under the document body must not dismiss the active dialog.
          if (
            target.closest(".tsqd-parent-container") ||
            target.closest("[data-voice-transcription-pill]") ||
            target.closest(".picker-dialog") ||
            target.closest(".picker-dialog-bg")
          ) {
            ev.preventDefault()
            return
          }

          // Only a guarded dialog (task, note, email) blocks the outside click and routes it
          // through its handler — unsaved changes show the "Discard?" confirm, a clean one closes.
          // Every other dialog keeps Radix's default outside-click-to-close behavior.
          const handler = getCloseAttemptHandler()
          if (handler) {
            ev.preventDefault()
            handler()
          }
        },
      }}
    >
      <Suspense
        fallback={
          <div className="flex w-full flex-col">
            <div className="flex h-12 items-center justify-between border-b px-5">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="size-6 rounded-full" />
            </div>
            <div className="space-y-3 p-5">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
              <Skeleton className="h-20 w-full" />
            </div>
          </div>
        }
      >
        <DialogBookContext key={contentStack[0]?.id} initialBookId={contentStack[0]?.bookId}>
          {contentStack.map((content, index) => {
            const active = index === contentStack.length - 1
            return (
              <DialogEntryContext.Provider key={content.id} value={content.id}>
                <div
                  ref={active ? activeContentRef : undefined}
                  className={active ? "contents" : "hidden"}
                  aria-hidden={!active}
                  inert={!active}
                >
                  {content.bookId ? (
                    <BookIdProvider bookId={content.bookId}>{content.content}</BookIdProvider>
                  ) : (
                    content.content
                  )}
                </div>
              </DialogEntryContext.Provider>
            )
          })}
        </DialogBookContext>
      </Suspense>
    </AppDialog>
  )
}
